import React from 'react';
import { AlertCircle } from 'lucide-react';
import GaugeChart from 'react-gauge-chart'; // Ensure this package is imported

const CreditAssessmentWidget = ({ crifV4Error, report, creditScore = 0, maxScore = 900 }) => {
  return (
    <div className="flex flex-col items-center w-full bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm">
      
      {/* Error Block Section */}
      {(() => {
        const message =
          crifV4Error?.error?.credit_report_message?.message ||
          crifV4Error?.message ||
          report?.status?.message ||
          "";

        const isSuccess = message?.toLowerCase().includes("success");
        if (isSuccess || !message) return null;

        const formatText = (text) =>
          text
            ?.replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());

        return (
          <div className="w-full flex justify-center mb-5">
            <div className="flex items-center gap-2.5 bg-rose-50/80 border border-rose-200 text-rose-900 px-4 py-3 rounded-xl text-sm w-full md:max-w-md shadow-3xs">
              <AlertCircle size={16} className="text-rose-500 shrink-0" />
              <p className="font-medium leading-normal">
                <span className="font-bold text-rose-900">Error:</span>{" "}
                {formatText(message)}
              </p>
            </div>
          </div>
        );
      })()}

      {/* Header Label / Title Block */}
      <div className="text-center flex flex-col items-center space-y-1 mb-8">
        <p className="text-xs font-bold text-indigo-600 uppercase tracking-widest">
          Credit Assessment
        </p>
        <h2 className="text-xl md:text-2xl font-extrabold text-slate-900 tracking-tight">
          Hey, {report?.data?.customer_identity?.name || "User"}!
        </h2>
      </div>

      {/* Scaled-down Compact Gauge Container */}
      <div className="w-full max-w-[260px] relative flex flex-col items-center mb-2 select-none">
        
        {/* Recalibrated Gauge Labels for Compact Dimension */}
        <span className="text-[10px] font-bold tracking-wider uppercase text-red-500 absolute top-[44px] -left-2 -rotate-45">
          Poor
        </span>
        <span className="text-[10px] font-bold tracking-wider uppercase text-amber-500 absolute top-[-6px] left-[42px] -rotate-12">
          Average
        </span>
        <span className="text-[10px] font-bold tracking-wider uppercase text-orange-500 absolute top-[-6px] right-[42px] rotate-12">
          Good
        </span>
        <span className="text-[10px] font-bold tracking-wider uppercase text-emerald-600 absolute top-[44px] -right-2 rotate-45">
          Excellent
        </span>

        {/* Gauge Component Instance */}
        <div className="w-full">
          <GaugeChart
            id="credit-gauge-compact"
            nrOfLevels={4}
            percent={maxScore > 0 ? creditScore / maxScore : 0}
            needleColor="#0f172a" // slate-900
            textColor="transparent"
            needleBaseColor="#0f172a"
            arcsLength={[0.25, 0.25, 0.25, 0.25]}
            colors={["#ef4444", "#f59e0b", "#f97316", "#10b981"]} // Modern Tailwind Semantic Tones
            arcWidth={0.18} // Adjusted line width fraction for a cleaner look
            animate={true}
          />
        </div>

        {/* Dynamic Big Score Indicator Centered inside Arc */}
        <div className="absolute bottom-5 flex flex-col items-center justify-center">
          <span className="text-3xl font-black text-slate-900 tracking-tight">
            {creditScore}
          </span>
          <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mt-0.5">
            CRIF Score
          </span>
        </div>
        
      </div>
    </div>
  );
};

export default CreditAssessmentWidget;